import { FC, useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { InstallPrompt } from '@components/shared/InstallPrompt'
import { DelayedQueryLoadingOverlay } from '@components/shared/DelayedQueryLoadingOverlay'
import { TransferExpiringAlert } from '@components/shared/TransferExpiringAlert'
import { DemoModeBanner } from '@components/shared/DemoModeBanner'
import { PdfViewerModal } from '@components/shared/PdfViewerModal'
// import { ChatWidget } from '@components/chat/ChatWidget'
import { cn } from '@utils/index'

const STORAGE_KEY = 'ecofarte_sidebar_collapsed'

export const MainLayout: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed))
    } catch {
      // ignore
    }
  }, [collapsed])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  const closeSidebar = () => setSidebarOpen(false)
  const toggleCollapsed = () => setCollapsed(prev => !prev)

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <InstallPrompt />
      <Header 
        onToggleSidebar={() => setSidebarOpen(prev => !prev)}
        onToggleCollapse={toggleCollapsed}
        collapsed={collapsed}
      />

      <div className="flex">
        {/* Mobile overlay sidebar */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 pt-16 transform transition-transform duration-300 ease-in-out lg:hidden",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <Sidebar onNavigate={closeSidebar} collapsed={false} />
        </aside>

        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden lg:block sticky top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 ease-in-out",
            collapsed ? "w-16 overflow-visible" : "w-64 overflow-hidden"
          )}
        >
          <Sidebar collapsed={collapsed} />
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm lg:hidden"
            onClick={closeSidebar}
          />
        )}

        <main className={cn(
          "flex-1 min-h-[calc(100vh-4rem)] transition-all duration-300",
          collapsed ? "lg:ml-0" : "lg:ml-0"
        )}>
          <div className="p-4 lg:p-6 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
      {/* <ChatWidget /> — ocultado temporalmente, hasta nuevo aviso */}
      <DelayedQueryLoadingOverlay />
      <TransferExpiringAlert />
      <DemoModeBanner />
      <PdfViewerModal />
    </div>
  )
}
